import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface SessionState {
  token: string | null;
  setToken: (token: string) => void;
  clear: () => void;
}

/**
 * The bearer token for THIS app, and nothing else.
 *
 * Deliberately not the user: the signed-in user is server state, so it belongs
 * to TanStack Query (`useCurrentUser`, backed by `GET /v1/users/me`), not to a
 * store that would start going stale the moment anything about them changed.
 * The token is the exception because it is the one thing the app must be able
 * to read synchronously — route guards and the fetch client both need it before
 * any query can run.
 *
 * The storage key is namespaced `opengoal.platform.*` on purpose. The backend
 * runs two independent better-auth instances with separate secrets and separate
 * session tables; an admin token is not valid here and must never be reachable
 * from this app's storage. Do not generalise this into a shared package.
 */
export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      token: null,
      setToken: (token) => set({ token }),
      clear: () => set({ token: null }),
    }),
    { name: 'opengoal.platform.session' },
  ),
);

/** Imperative accessor for non-React code (the fetch client, route guards). */
export function getSessionToken(): string | null {
  return useSessionStore.getState().token;
}

export function setSessionToken(token: string): void {
  useSessionStore.getState().setToken(token);
}

export function clearSession(): void {
  useSessionStore.getState().clear();
}

/**
 * Whether a token is present — NOT whether it is still valid.
 *
 * Route guards run synchronously, so they cannot verify against the server.
 * A revoked or expired token therefore renders the shell once and is then
 * caught by the first request's 401, which clears the session and redirects.
 */
export function isSignedIn(): boolean {
  return getSessionToken() !== null;
}
